import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import BreathingStage from '../components/BreathingStage';
import ApneaStage from '../components/ApneaStage';
import DrawingCanvas from '../components/DrawingCanvas';
import { Check, ArrowRight, Eye, TrendingUp, TrendingDown, Sparkles, Volume2, Users } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function Sesion() {
  const { id } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  // Steps: 1: breathing, 1.5: apnea (only for blind/custom), 2: instructions, 3: impressions, 4: pick, 5: reveal, 6: result
  const [step, setStep] = useState(1);
  const [apneaDuration, setApneaDuration] = useState(0);
  // Theta audio ref (plays throughout session)
  const thetaAudioRef = useRef(null);
  
  // Form state
  const [viewerNote, setViewerNote] = useState('');
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [scoreA, setScoreA] = useState(0);
  const [scoreB, setScoreB] = useState(0);
  const [isPass, setIsPass] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [revealData, setRevealData] = useState(null);
  const [consensusData, setConsensusData] = useState(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    fetchSession();
    
    // Start theta audio on mount
    if (thetaAudioRef.current) {
      thetaAudioRef.current.volume = 0.3;
      thetaAudioRef.current.loop = true;
      thetaAudioRef.current.play().catch(() => {});
    }
    
    return () => {
      // Stop audio on unmount
      if (thetaAudioRef.current) {
        thetaAudioRef.current.pause();
      }
    };
  }, [id]);

  // Fetch consensus data for blind sessions when showing result
  useEffect(() => {
    if (session?.session_type === 'blind' && session?.result && session.result !== 'pending') {
      fetchConsensus();
    }
  }, [session?.result]);

  const fetchConsensus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/consensus/session/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.has_consensus) {
          setConsensusData(data);
        }
      }
    } catch (error) {
      console.error('Error fetching consensus:', error);
    }
  };

  useEffect(() => {
    fetchSession();
  }, [id]);

  const fetchSession = async () => {
    try {
      const response = await fetch(`${API_URL}/api/sessions/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Sesión no encontrada');
      }
      
      setSession(data);
      
      // Check if it's an advanced session
      const isAdvanced = data.session_type === 'advanced';
      
      // Determine current step based on session state
      if (data.result !== 'pending') {
        setStep(6); // Show result
      } else if (data.viewer_pick) {
        setStep(5); // Show reveal
        setRevealData({
          up_image_is: data.up_image_is,
          predicted_dir: data.predicted_dir
        });
      } else if (data.viewer_note) {
        setStep(4); // Pick targets
      } else if (data.instructions_shown) {
        // Advanced sessions skip impressions, go to pick (step 4)
        // Normal sessions go to impressions (step 3)
        setStep(isAdvanced ? 4 : 3);
      } else if (data.breathing_completed) {
        // Advanced sessions skip apnea, go to instructions (step 2)
        // Normal sessions go to apnea (step 1.5) - but if returning, go to instructions
        setStep(2);
      } else {
        setStep(1); // Breathing
      }
    } catch (error) {
      toast.error(error.message || 'Error al cargar sesión');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleBreathingComplete = async () => {
    try {
      await fetch(`${API_URL}/api/sessions/${id}/breathing`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // For advanced sessions, skip apnea - go directly to instructions (step 2)
      if (session?.session_type === 'advanced') {
        setStep(2); // Go to instructions/tasking
      } else {
        // For blind and custom sessions, go to apnea
        setStep(1.5); // Go to apnea stage
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleApneaComplete = async (duration) => {
    setApneaDuration(duration);
    // After apnea, go to instructions
    setStep(2);
  };

  const handleInstructionsComplete = async () => {
    try {
      await fetch(`${API_URL}/api/sessions/${id}/instructions`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // For advanced sessions, skip impressions - go directly to images (step 4)
      if (session?.session_type === 'advanced') {
        setStep(4); // Go to target selection (images)
      } else {
        setStep(3); // Go to impressions
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleSubmitImpressions = async () => {
    if (!viewerNote.trim()) {
      toast.error('Por favor describe tus impresiones');
      return;
    }
    
    setSubmitting(true);
    try {
      // Get drawing data from canvas
      let sketchData = null;
      if (canvasRef.current && !canvasRef.current.isEmpty()) {
        sketchData = canvasRef.current.getDrawingData();
      }

      const response = await fetch(`${API_URL}/api/sessions/${id}/impressions`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          viewer_note: viewerNote,
          sketch_data: sketchData
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Error al guardar');
      }
      
      setStep(4);
    } catch (error) {
      toast.error(error.message || 'Error al guardar impresiones');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitPick = async () => {
    // Determine viewer_pick from scores
    const viewer_pick = scoreA > scoreB ? 'A' : scoreB > scoreA ? 'B' : 'A';
    const confidence = Math.abs(scoreA - scoreB);
    
    // Check if should be a pass
    const shouldPass = isPass || confidence < 2;
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/sessions/${id}/pick`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          viewer_pick: viewer_pick,
          confidence: confidence,
          score_image_a: scoreA,
          score_image_b: scoreB,
          is_pass: shouldPass
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Error al enviar');
      }
      
      setRevealData(data);
      
      // Refresh session to get the instrument info
      const sessionResponse = await fetch(`${API_URL}/api/sessions/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const sessionData = await sessionResponse.json();
      if (sessionResponse.ok) {
        setSession(sessionData);
      }
      
      setStep(5);
    } catch (error) {
      toast.error(error.message || 'Error al enviar elección');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-white/70">Cargando sesión...</div>
      </div>
    );
  }

  if (!session) return null;

  const isAdvancedSession = session.session_type === 'advanced';
  const isCustomSession = session.session_type === 'custom';
  const accentColor = isAdvancedSession ? 'hsl(45,93%,47%)' : isCustomSession ? 'hsl(252,37%,60%)' : 'hsl(198,62%,54%)';

  // Define steps based on session type
  // Advanced: 1 (breathing) -> 4 (pick) -> 5 (reveal) -> 6 (result)
  // Blind/Custom: 1 (breathing) -> 1.5 (apnea) -> 2 (instructions) -> 3 (impressions) -> 4 (pick) -> 5 (reveal) -> 6 (result)
  const getStepLabel = () => {
    if (isAdvancedSession) {
      return { 1: 'Respiración', 4: 'Elegir', 5: 'Revelar', 6: 'Resultado' };
    }
    return { 1: 'Respiración', 1.5: 'Apnea', 2: 'Enfoque', 3: 'Impresiones', 4: 'Elegir', 5: 'Revelar', 6: 'Resultado' };
  };

  const stepNumbers = isAdvancedSession ? [1, 4, 5, 6] : [1, 1.5, 2, 3, 4, 5, 6];
  const currentStepIndex = stepNumbers.indexOf(step);

  // Format target date/time
  const formatTargetDateTime = () => {
    if (!session?.target_date_utc) return null;
    const target = new Date(session.target_date_utc);
    return {
      date: target.toLocaleDateString('es-ES', {
        weekday: 'long',
        day: 'numeric',
        month: 'long'
      }),
      time: target.toLocaleTimeString('es-ES', {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'UTC'
      }) + ' UTC'
    };
  };

  // Calculate time remaining
  const getTimeRemaining = () => {
    if (!session?.target_date_utc) return null;
    const target = new Date(session.target_date_utc);
    const now = new Date();
    const diff = target - now;
    
    if (diff <= 0) return { text: 'Verificando...', isOverdue: true };
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return { text: `${days}d ${hours % 24}h`, isOverdue: false };
    }
    return { text: `${hours}h ${minutes}m`, isOverdue: false };
  };

  const targetDateTime = formatTargetDateTime();
  const timeRemaining = getTimeRemaining();

  return (
    <div className="p-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-start justify-between">
          <div>
            <p className={`text-sm font-medium mb-1`} style={{ color: accentColor }}>
              {isAdvancedSession ? 'Sesión avanzada' : isCustomSession ? 'Sesión personalizada' : 'Sesión ciega'}
            </p>
            <h1 className="text-4xl font-bold text-white" data-testid="session-code">
              {session.session_code}
            </h1>
          </div>
          
          {/* Target time info - Always visible */}
          {targetDateTime && step < 6 && (
            <div className="text-right bg-white/5 rounded-xl px-4 py-3">
              <p className="text-xs text-white/40 uppercase tracking-wider mb-1">Verificación</p>
              <p className="text-white font-medium capitalize">{targetDateTime.date}</p>
              <p className="text-white/70 text-sm">{targetDateTime.time}</p>
              {timeRemaining && !timeRemaining.isOverdue && (
                <p className="text-[hsl(198,62%,54%)] text-sm mt-1 font-medium">
                  Faltan {timeRemaining.text}
                </p>
              )}
              {timeRemaining?.isOverdue && (
                <p className="text-[hsl(45,93%,47%)] text-sm mt-1 font-medium animate-pulse">
                  {timeRemaining.text}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center gap-2 mb-10">
        {stepNumbers.map((s, idx) => (
          <React.Fragment key={s}>
            <div 
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                currentStepIndex > idx 
                  ? 'bg-[hsl(160,84%,39%)] text-white' 
                  : step === s 
                  ? 'text-white' 
                  : 'bg-white/10 text-white/40'
              }`}
              style={step === s ? { backgroundColor: accentColor } : {}}
              title={getStepLabel()[s]}
            >
              {currentStepIndex > idx ? <Check className="w-4 h-4" /> : idx + 1}
            </div>
            {idx < stepNumbers.length - 1 && (
              <div className={`flex-1 h-0.5 ${
                currentStepIndex > idx ? 'bg-[hsl(160,84%,39%)]' : 'bg-white/10'
              }`} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Step Content */}
      <div className="bg-[hsl(232,36%,9%)] border border-[hsl(232,18%,18%)] rounded-xl p-8">
        
        {/* Hidden theta audio for entire session */}
        <audio 
          ref={thetaAudioRef} 
          src={`${API_URL}/api/audio/theta_loop.mp3`} 
          loop 
          preload="auto"
        />
        
        {/* Step 1: Breathing */}
        {step === 1 && (
          <div className="text-center">
            <h2 className="text-2xl font-semibold text-white mb-2">
              {session?.session_type === 'advanced' ? 'Canalización Rápida' : 'Canalización Cortex'}
            </h2>
            <p className="text-white/50 mb-4">
              {session?.session_type === 'advanced' ? '30 respiraciones para centrar tu mente' : '40 respiraciones para centrar tu mente'}
            </p>
            <BreathingStage 
              onComplete={handleBreathingComplete} 
              totalBreaths={session?.session_type === 'advanced' ? 30 : 40}
              skipApnea={session?.session_type === 'advanced'}
            />
          </div>
        )}

        {/* Step 1.5: Apnea - Only for blind and custom sessions */}
        {step === 1.5 && (
          <div className="text-center">
            <h2 className="text-2xl font-semibold text-white mb-2">Apnea</h2>
            <p className="text-white/50 mb-8">Retención de la respiración</p>
            <ApneaStage onComplete={handleApneaComplete} />
          </div>
        )}

        {/* Step 2: Focus Instructions / Tasking - For ALL session types */}
        {step === 2 && (
          <div className="text-center" data-testid="instructions-stage">
            {/* TRN Header */}
            <div className="mb-6 py-3 px-6 bg-white/5 rounded-xl inline-block">
              <p className="text-xs text-white/40 uppercase tracking-wider">Número de Tarea</p>
              <p className="text-2xl font-mono font-bold text-[hsl(198,62%,54%)]">{session?.session_code}</p>
            </div>
            
            <div className="w-20 h-20 rounded-full bg-[hsl(252,37%,60%)]/20 flex items-center justify-center mx-auto mb-6">
              <Sparkles className="w-10 h-10 text-[hsl(252,37%,60%)]" />
            </div>
            
            <h2 className="text-2xl font-semibold text-white mb-4">🎯 Tu Tarea de Visualización</h2>
            
            <div className="max-w-lg mx-auto bg-[hsl(232,20%,14%)] rounded-xl p-6 mb-6 text-left border border-[hsl(232,18%,18%)]">
              <p className="text-white/90 leading-relaxed mb-4 text-center italic">
                "Describe la imagen que verás como FEEDBACK el{' '}
                <span className="text-[hsl(198,62%,54%)] font-medium">
                  {session?.target_date_utc ? new Date(session.target_date_utc).toLocaleDateString('es-ES', {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                    hour: '2-digit',
                    minute: '2-digit'
                  }) : 'próximamente'}
                </span>"
              </p>
              
              <div className="border-t border-white/10 pt-4 mt-4">
                <p className="text-white/80 leading-relaxed mb-3">
                  <span className="text-[hsl(45,93%,47%)]">•</span> Imagina que viajas al momento exacto en que la app te mostrará la foto ganadora
                </p>
                <p className="text-white/80 leading-relaxed mb-3">
                  <span className="text-[hsl(45,93%,47%)]">•</span> Registra lo que percibes: <strong className="text-white">formas, líneas, colores, texturas, sensaciones, emociones</strong>
                </p>
                <p className="text-white/80 leading-relaxed">
                  <span className="text-[hsl(45,93%,47%)]">•</span> <strong className="text-white">NO interpretes</strong>, solo registra las impresiones crudas
                </p>
              </div>
            </div>

            <p className="text-white/40 text-sm mb-6">
              Tómate el tiempo que necesites. Cuando estés listo, continúa.
            </p>

            <button
              onClick={handleInstructionsComplete}
              data-testid="continue-to-impressions-button"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-medium text-white transition-colors"
              style={{ backgroundColor: accentColor }}
            >
              Comenzar a Dibujar
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Step 3: Impressions */}
        {step === 3 && (
          <div>
            <h2 className="text-2xl font-semibold text-white mb-2">Describe tus impresiones</h2>
            <p className="text-white/50 mb-6">
              Sin pensar en el mercado, describe las sensaciones, formas, colores o imágenes que percibiste.
            </p>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm text-white/70 mb-2">Impresiones sensoriales</label>
                <textarea
                  value={viewerNote}
                  onChange={(e) => setViewerNote(e.target.value)}
                  data-testid="impressions-textarea"
                  className="w-full h-32 bg-[hsl(232,20%,14%)] border border-[hsl(232,18%,18%)] rounded-lg p-4 text-white placeholder-white/30 focus:outline-none focus:ring-2 resize-none"
                  style={{ '--tw-ring-color': accentColor }}
                  placeholder="Formas, texturas, temperaturas, colores, sonidos, emociones..."
                />
              </div>

              <div>
                <label className="block text-sm text-white/70 mb-2">Dibuja lo que percibiste (opcional)</label>
                <DrawingCanvas ref={canvasRef} />
              </div>

              <button
                onClick={handleSubmitImpressions}
                disabled={submitting || !viewerNote.trim()}
                data-testid="submit-impressions-button"
                className="w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-4 rounded-xl transition-colors"
                style={{ backgroundColor: accentColor }}
              >
                {submitting ? 'Guardando...' : 'Ver objetivos'}
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Dual Scoring */}
        {step === 4 && (
          <div>
            <h2 className="text-2xl font-semibold text-white mb-2">Evalúa la correspondencia</h2>
            <p className="text-white/50 mb-6">
              Compara tu dibujo con cada imagen y puntúa de 0 a 7 según su correspondencia
            </p>

            {/* User's sketch for reference */}
            {session.viewer_sketch_url && (
              <div className="mb-6 p-4 bg-[hsl(232,20%,14%)] rounded-xl">
                <p className="text-sm text-white/60 mb-2 text-center">Tu dibujo:</p>
                <div className="flex justify-center">
                  <img 
                    src={session.viewer_sketch_url.startsWith('http') ? session.viewer_sketch_url : `${API_URL}${session.viewer_sketch_url}`}
                    alt="Tu dibujo"
                    className="max-h-32 rounded-lg border border-white/10"
                  />
                </div>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-6 mb-8" data-testid="target-choices">
              {/* Image A */}
              <div className={`rounded-xl overflow-hidden border-2 transition-all ${
                scoreA > scoreB 
                  ? 'border-[hsl(160,84%,39%)] bg-[hsl(160,84%,39%)]/5' 
                  : 'border-[hsl(232,18%,18%)]'
              }`}>
                <div className="aspect-[4/3] bg-[hsl(232,20%,14%)]">
                  {session.image_a_url ? (
                    <img 
                      src={session.image_a_url.startsWith('http') ? session.image_a_url : `${API_URL}${session.image_a_url}`}
                      alt="Objetivo A"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Eye className="w-12 h-12 text-white/20" />
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <p className="font-medium text-white mb-3">Imagen A</p>
                  <div className="flex gap-1 justify-center">
                    {[0,1,2,3,4,5,6,7].map(score => (
                      <button
                        key={score}
                        onClick={() => setScoreA(score)}
                        data-testid={`score-a-${score}`}
                        className={`w-8 h-8 rounded-lg font-medium text-sm transition-all ${
                          scoreA === score 
                            ? 'bg-[hsl(198,62%,54%)] text-white' 
                            : 'bg-white/10 text-white/60 hover:bg-white/20'
                        }`}
                      >
                        {score}
                      </button>
                    ))}
                  </div>
                  <p className="text-center text-sm text-white/40 mt-2">Puntuación: {scoreA}</p>
                </div>
              </div>

              {/* Image B */}
              <div className={`rounded-xl overflow-hidden border-2 transition-all ${
                scoreB > scoreA 
                  ? 'border-[hsl(160,84%,39%)] bg-[hsl(160,84%,39%)]/5' 
                  : 'border-[hsl(232,18%,18%)]'
              }`}>
                <div className="aspect-[4/3] bg-[hsl(232,20%,14%)]">
                  {session.image_b_url ? (
                    <img 
                      src={session.image_b_url.startsWith('http') ? session.image_b_url : `${API_URL}${session.image_b_url}`}
                      alt="Objetivo B"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Eye className="w-12 h-12 text-white/20" />
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <p className="font-medium text-white mb-3">Imagen B</p>
                  <div className="flex gap-1 justify-center">
                    {[0,1,2,3,4,5,6,7].map(score => (
                      <button
                        key={score}
                        onClick={() => setScoreB(score)}
                        data-testid={`score-b-${score}`}
                        className={`w-8 h-8 rounded-lg font-medium text-sm transition-all ${
                          scoreB === score 
                            ? 'bg-[hsl(252,37%,60%)] text-white' 
                            : 'bg-white/10 text-white/60 hover:bg-white/20'
                        }`}
                      >
                        {score}
                      </button>
                    ))}
                  </div>
                  <p className="text-center text-sm text-white/40 mt-2">Puntuación: {scoreB}</p>
                </div>
              </div>
            </div>

            {/* Difference and Pass Warning */}
            <div className="mb-6 p-4 rounded-xl bg-[hsl(232,20%,14%)] text-center">
              <p className="text-sm text-white/60 mb-1">Diferencia entre puntuaciones</p>
              <p className={`text-2xl font-bold ${
                Math.abs(scoreA - scoreB) >= 2 ? 'text-[hsl(160,84%,50%)]' : 'text-[hsl(33,92%,55%)]'
              }`}>
                {Math.abs(scoreA - scoreB)} puntos
              </p>
              
              {Math.abs(scoreA - scoreB) < 2 && (scoreA > 0 || scoreB > 0) && (
                <div className="mt-3 p-3 bg-[hsl(33,92%,55%)]/10 border border-[hsl(33,92%,55%)]/30 rounded-lg">
                  <p className="text-[hsl(33,92%,55%)] text-sm font-medium">
                    ⚠️ PASE RECOMENDADO: La diferencia es muy pequeña
                  </p>
                  <p className="text-white/60 text-xs mt-1">
                    Puedes continuar, pero esta predicción es débil
                  </p>
                </div>
              )}
              
              {scoreA < 3 && scoreB < 3 && (scoreA > 0 || scoreB > 0) && (
                <div className="mt-3 p-3 bg-[hsl(33,92%,55%)]/10 border border-[hsl(33,92%,55%)]/30 rounded-lg">
                  <p className="text-[hsl(33,92%,55%)] text-sm font-medium">
                    ⚠️ PASE RECOMENDADO: No hay correspondencia clara
                  </p>
                </div>
              )}
            </div>

            {/* Pass checkbox */}
            <div className="mb-6">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isPass}
                  onChange={(e) => setIsPass(e.target.checked)}
                  className="w-5 h-5 rounded border-2 border-white/30 bg-transparent"
                />
                <span className="text-white/70">Declarar PASE (no realizar predicción)</span>
              </label>
            </div>

            <button
              onClick={handleSubmitPick}
              disabled={submitting || (scoreA === 0 && scoreB === 0)}
              data-testid="submit-pick-button"
              className="w-full disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-4 rounded-xl transition-colors"
              style={{ backgroundColor: accentColor }}
            >
              {submitting ? 'Enviando...' : isPass ? 'Declarar Pase' : `Enviar evaluación (Predicción: ${scoreA > scoreB ? 'Imagen A' : 'Imagen B'})`}
            </button>
          </div>
        )}

        {/* Step 5: Reveal */}
        {step === 5 && revealData && (
          <div className="text-center">
            <h2 className="text-2xl font-semibold text-white mb-6">
              {revealData.is_pass ? 'Sesión Cerrada' : 'Revelación'}
            </h2>
            
            {/* PASS case - session closed immediately */}
            {revealData.is_pass ? (
              <div>
                <div className="w-20 h-20 rounded-full bg-[hsl(33,92%,55%)]/20 flex items-center justify-center mx-auto mb-6">
                  <span className="text-4xl">⏭️</span>
                </div>
                
                <div className="p-6 bg-[hsl(33,92%,55%)]/10 border border-[hsl(33,92%,55%)]/30 rounded-xl mb-6">
                  <p className="text-xl font-bold text-[hsl(33,92%,55%)] mb-2">PASE DECLARADO</p>
                  <p className="text-white/70">
                    No se realizó predicción. La sesión se ha cerrado sin esperar verificación.
                  </p>
                  <p className="text-white/50 text-sm mt-3">
                    Diferencia de puntuación: {revealData.confidence} puntos
                  </p>
                </div>
                
                <p className="text-white/50 mb-6">
                  Puedes iniciar una nueva sesión cuando quieras.
                </p>
                
                <div className="flex gap-4 justify-center">
                  <button
                    onClick={() => navigate('/nueva-sesion')}
                    className="bg-[hsl(198,62%,54%)] hover:bg-[hsl(198,62%,48%)] text-white px-6 py-3 rounded-xl transition-colors"
                  >
                    Nueva sesión
                  </button>
                  <button
                    onClick={() => navigate('/')}
                    className="bg-white/10 hover:bg-white/15 text-white px-6 py-3 rounded-xl transition-colors"
                  >
                    Ir al dashboard
                  </button>
                </div>
              </div>
            ) : (
              /* Normal prediction reveal */
              <div>
                {/* Instrument reveal */}
                {session.symbol && (
                  <div className="bg-[hsl(232,20%,14%)] rounded-xl p-4 mb-6">
                    <p className="text-white/50 text-sm mb-1">Activo predicho</p>
                    <p className="text-xl font-bold text-white">{session.symbol}</p>
                    <p className="text-white/40 text-sm mt-1">
                      {session.instrument_type === 'crypto' ? 'Criptomoneda' : 'Forex'}
                    </p>
                  </div>
                )}
                
                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  <div className={`p-6 rounded-xl border-2 ${
                    revealData.up_image_is === 'A' 
                      ? 'border-[hsl(160,84%,39%)] bg-[hsl(160,84%,39%)]/10' 
                      : 'border-[hsl(0,70%,50%)] bg-[hsl(0,70%,50%)]/10'
                  }`}>
                    <p className="text-sm text-white/50 mb-2">Objetivo A</p>
                    <div className="flex items-center justify-center gap-2">
                      {revealData.up_image_is === 'A' ? (
                        <><TrendingUp className="w-6 h-6 text-[hsl(160,84%,50%)]" /><span className="text-xl font-bold text-[hsl(160,84%,50%)]">SUBIDA</span></>
                      ) : (
                        <><TrendingDown className="w-6 h-6 text-[hsl(0,70%,60%)]" /><span className="text-xl font-bold text-[hsl(0,70%,60%)]">BAJADA</span></>
                      )}
                    </div>
                  </div>

                  <div className={`p-6 rounded-xl border-2 ${
                    revealData.up_image_is === 'B' 
                      ? 'border-[hsl(160,84%,39%)] bg-[hsl(160,84%,39%)]/10' 
                      : 'border-[hsl(0,70%,50%)] bg-[hsl(0,70%,50%)]/10'
                  }`}>
                    <p className="text-sm text-white/50 mb-2">Objetivo B</p>
                    <div className="flex items-center justify-center gap-2">
                      {revealData.up_image_is === 'B' ? (
                        <><TrendingUp className="w-6 h-6 text-[hsl(160,84%,50%)]" /><span className="text-xl font-bold text-[hsl(160,84%,50%)]">SUBIDA</span></>
                      ) : (
                        <><TrendingDown className="w-6 h-6 text-[hsl(0,70%,60%)]" /><span className="text-xl font-bold text-[hsl(0,70%,60%)]">BAJADA</span></>
                      )}
                    </div>
                  </div>
                </div>

                <div className="p-6 bg-[hsl(232,20%,14%)] rounded-xl mb-6">
                  <p className="text-white/50 mb-2">Tu predicción para {session.symbol}</p>
                  <p className="text-3xl font-bold text-white" data-testid="predicted-direction">
                    {revealData.predicted_dir === 'UP' ? '↑ SUBIDA' : '↓ BAJADA'}
                  </p>
                  <p className="text-sm text-white/40 mt-2">
                    Precio base: ${session.baseline_price?.toFixed(session.instrument_type === 'forex' ? 5 : 2)}
                  </p>
                  <p className="text-sm text-white/40 mt-1">
                    El resultado se revelará en {session.horizon_hours} horas
                  </p>
                </div>

                <button
                  onClick={() => navigate('/')}
                  className="bg-white/10 hover:bg-white/15 text-white px-6 py-3 rounded-xl transition-colors"
                >
                  Volver al dashboard
                </button>
              </div>
            )}
          </div>
        )}

        {/* Step 6: Final Result */}
        {step === 6 && (
          <div className="text-center">
            <h2 className="text-2xl font-semibold text-white mb-6">Resultado Final</h2>
            
            <div className={`inline-block px-8 py-6 rounded-2xl mb-8 ${
              session.result === 'hit' 
                ? 'bg-[hsl(160,84%,39%)]/20' 
                : session.result === 'miss'
                ? 'bg-[hsl(0,70%,50%)]/20'
                : 'bg-[hsl(33,92%,55%)]/20'
            }`}>
              <p className={`text-4xl font-bold ${
                session.result === 'hit' 
                  ? 'text-[hsl(160,84%,50%)]' 
                  : session.result === 'miss'
                  ? 'text-[hsl(0,70%,60%)]'
                  : 'text-[hsl(33,92%,60%)]'
              }`} data-testid="session-result">
                {session.result === 'hit' ? '✓ ACIERTO' : session.result === 'miss' ? '✗ FALLO' : '∼ NULO'}
              </p>
            </div>

            {session.symbol && (
              <div className="grid md:grid-cols-3 gap-4 mb-8">
                <div className="p-4 bg-[hsl(232,20%,14%)] rounded-xl">
                  <p className="text-sm text-white/50 mb-1">Activo</p>
                  <p className="text-lg font-medium text-white">{session.symbol}</p>
                </div>
                <div className="p-4 bg-[hsl(232,20%,14%)] rounded-xl">
                  <p className="text-sm text-white/50 mb-1">Precio inicial</p>
                  <p className="text-lg font-medium text-white">
                    ${session.baseline_price?.toFixed(session.instrument_type === 'forex' ? 5 : 2)}
                  </p>
                </div>
                <div className="p-4 bg-[hsl(232,20%,14%)] rounded-xl">
                  <p className="text-sm text-white/50 mb-1">Precio final</p>
                  <p className="text-lg font-medium text-white">
                    ${session.verification_price?.toFixed(session.instrument_type === 'forex' ? 5 : 2)}
                  </p>
                </div>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-4 mb-8">
              <div className="p-4 bg-[hsl(232,20%,14%)] rounded-xl">
                <p className="text-sm text-white/50 mb-1">Tu predicción</p>
                <p className="text-lg font-medium text-white">
                  {session.predicted_dir === 'UP' ? '↑ Subida' : '↓ Bajada'}
                </p>
              </div>
              <div className="p-4 bg-[hsl(232,20%,14%)] rounded-xl">
                <p className="text-sm text-white/50 mb-1">Movimiento real</p>
                <p className="text-lg font-medium text-white">
                  {session.actual_dir === 'UP' ? '↑ Subida' : session.actual_dir === 'DOWN' ? '↓ Bajada' : '∼ Plano'}
                </p>
              </div>
            </div>

            {/* Consensus section for blind sessions */}
            {session.session_type === 'blind' && consensusData && (
              <div className="mb-8 p-6 bg-[hsl(252,37%,60%)]/10 border border-[hsl(252,37%,60%)]/30 rounded-xl">
                <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-[hsl(252,37%,60%)]" />
                  Consenso Colectivo
                </h3>
                
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-[hsl(160,84%,39%)]">{consensusData.up_votes}</p>
                    <p className="text-xs text-white/50">Votos UP</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-red-400">{consensusData.down_votes}</p>
                    <p className="text-xs text-white/50">Votos DOWN</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-white">{consensusData.total_participants}</p>
                    <p className="text-xs text-white/50">Participantes</p>
                  </div>
                </div>
                
                <div className="flex items-center justify-center gap-3">
                  <span className="text-white/60">Consenso:</span>
                  <span className={`font-bold ${
                    consensusData.consensus_direction === 'UP' 
                      ? 'text-[hsl(160,84%,39%)]' 
                      : consensusData.consensus_direction === 'DOWN'
                      ? 'text-red-400'
                      : 'text-white/50'
                  }`}>
                    {consensusData.consensus_direction === 'UP' ? '↑ SUBIDA' : 
                     consensusData.consensus_direction === 'DOWN' ? '↓ BAJADA' : 
                     'EMPATE'}
                  </span>
                  <span className="text-white/40">({consensusData.consensus_percentage}%)</span>
                  
                  {consensusData.consensus_result && (
                    <span className={`ml-2 px-2 py-1 rounded text-xs font-medium ${
                      consensusData.consensus_result === 'hit' 
                        ? 'bg-[hsl(160,84%,39%)]/20 text-[hsl(160,84%,45%)]' 
                        : 'bg-red-500/20 text-red-400'
                    }`}>
                      {consensusData.consensus_result === 'hit' ? '✓ Acertó' : '✗ Falló'}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Link to view feedback for hits */}
            {session.result === 'hit' && (
              <button
                onClick={() => navigate(`/sesion/${id}/feedback`)}
                className="mb-4 text-[hsl(198,62%,54%)] hover:underline"
              >
                Ver feedback →
              </button>
            )}

            <button
              onClick={() => navigate('/')}
              className="text-white px-6 py-3 rounded-xl transition-colors"
              style={{ backgroundColor: accentColor }}
            >
              Volver al dashboard
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
